import { getConfig } from './config';
import { getIdToken } from './auth';

export type TicketSummary = {
  ticket_id: string;
  status: string;
  product?: string;
  confidence?: number;
  subject?: string;
  from?: string;
  mail_date?: string;
  created_at?: number;
  updated_at?: number;
  rischio?: string;
};

export type Ticket = {
  ticket_id: string;
  status: string;
  product?: string;
  confidence?: number;
  mail?: {
    from?: string;
    subject?: string;
    body_text?: string;
    body_html?: string;
    date?: string;
    message_id?: string;
  };
  sender_email?: string;
  proposed_sender_email?: string;
  draft_customer?: string;
  draft_proposed?: string;
  draft_dirty?: boolean;
  dossier?: Record<string, unknown>;
  chat?: Array<{ role: string; content: string; ts?: number; meta?: Record<string, unknown> }>;
  chat_turns?: number;
  updated_at?: number;
};

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const cfg = getConfig();
  const token = await getIdToken();
  if (!token && !localStorage.getItem('supporto_dev_user')) {
    throw new Error('Sessione scaduta: effettua di nuovo l’accesso.');
  }
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string>),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${cfg.apiBaseUrl.replace(/\/$/, '')}${path}`, {
      ...init,
      headers,
    });
  } catch (err) {
    throw new Error(
      `Connessione API fallita (${cfg.apiBaseUrl}). ${err instanceof Error ? err.message : String(err)}`,
    );
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok && res.status !== 202) {
    throw new Error((data as { error?: string }).error || `HTTP ${res.status}`);
  }
  return data as T;
}

export const listTickets = (status?: string, limit = 200) =>
  api<{ tickets: TicketSummary[] }>(
    `/tickets?limit=${limit}${status ? `&status=${encodeURIComponent(status)}` : ''}`,
  );

export const getTicket = (id: string) => api<{ ticket: Ticket }>(`/tickets/${id}`);

export const sendChat = (id: string, message: string) =>
  api<{ ticket: Ticket; async?: boolean; ok?: boolean; message?: string }>(`/tickets/${id}/chat`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });

export const saveDraft = (id: string, draft_customer: string, sender_email?: string) =>
  api<{ ticket: Ticket }>(`/tickets/${id}/draft`, {
    method: 'PUT',
    body: JSON.stringify(
      sender_email != null && sender_email.trim()
        ? { draft_customer, sender_email: sender_email.trim() }
        : { draft_customer },
    ),
  });

export const applyProposedDraft = (id: string) =>
  api<{ ticket: Ticket }>(`/tickets/${id}/apply-proposed-draft`, { method: 'POST', body: '{}' });

export const setStatus = (id: string, status: string) =>
  api<{ ticket: Ticket }>(`/tickets/${id}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });

export const syncDrafts = (id: string, draft_customer?: string) =>
  api<{ ticket: Ticket; synced: boolean; folder?: string; to?: string; message?: string }>(
    `/tickets/${id}/sync-drafts`,
    {
      method: 'POST',
      body: JSON.stringify(draft_customer != null ? { draft_customer } : {}),
    },
  );

export const sendReply = (id: string, draft_customer?: string) =>
  api<{ ticket: Ticket; sent: boolean; to?: string; message?: string }>(
    `/tickets/${id}/send-reply`,
    {
      method: 'POST',
      body: JSON.stringify(draft_customer != null ? { draft_customer } : {}),
    },
  );

export const reanalyzeTicket = (id: string) =>
  api<{ ticket: Ticket; async?: boolean; ok?: boolean; message?: string }>(
    `/tickets/${id}/reanalyze`,
    {
      method: 'POST',
      body: '{}',
    },
  );

export const pollMail = () =>
  api<{ ok: boolean; async?: boolean; message?: string }>('/poll-mail', {
    method: 'POST',
    body: '{}',
  });
